import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

/**
 * Renders text with LaTeX math notation
 * @param {string} children - Text with LaTeX delimiters ($...$ or $$...$$)
 * @returns {JSX.Element} - Rendered content
 */
export function MathText({ children }) {
  if (!children || typeof children !== 'string') {
    return <>{children}</>;
  }

  // Split by block math first ($$...$$)
  // eslint-disable-next-line no-useless-escape
  const blockParts = children.split(/(\$\$[^\$]+\$\$)/);

  return (
    <>
      {blockParts.map((blockPart, blockIdx) => {
        // Check if this part is block math
        if (blockPart.startsWith('$$') && blockPart.endsWith('$$')) {
          const latex = blockPart.slice(2, -2);
          return (
            <div key={blockIdx} className="my-4">
              <BlockMath math={latex} />
            </div>
          );
        }

        // Split by inline math ($...$)
        // eslint-disable-next-line no-useless-escape
        const inlineParts = blockPart.split(/(\$[^\$]+\$)/);

        return inlineParts.map((inlinePart, inlineIdx) => {
          // Check if this part is inline math
          if (inlinePart.startsWith('$') && inlinePart.endsWith('$')) {
            const latex = inlinePart.slice(1, -1);
            return <InlineMath key={`${blockIdx}-${inlineIdx}`} math={latex} />;
          }

          // Regular text
          return <span key={`${blockIdx}-${inlineIdx}`}>{inlinePart}</span>;
        });
      })}
    </>
  );
}

/**
 * Error boundary for LaTeX rendering
 */
export class MathErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <span className="text-red-500 text-sm">
          ⚠️ Invalid math syntax: {this.state.error?.message}
        </span>
      );
    }

    return this.props.children;
  }
}

// Export wrapped version
export function SafeMathText({ children, text }) {
  const content = text || children;
  return (
    <MathErrorBoundary>
      <MathText>{content}</MathText>
    </MathErrorBoundary>
  );
}
